"use client";

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, ArrowLeft, Loader2, Shield, CheckCircle } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/forgot-password', { email });
      setSent(true);
      toast.success('Password reset link sent!');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to send reset link.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] bg-indigo-950 overflow-hidden flex-col p-12 sticky top-0 h-screen">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?q=80&w=2070&auto=format&fit=crop')",
          backgroundSize: 'cover', backgroundPosition: 'center',
        }} />
        <div className="relative z-10 mb-auto">
          <div className="flex flex-col">
            <img src="/assets/logo/logo.svg" className="h-10 w-auto object-contain brightness-0 invert" alt="ShoplyCircle Logo" />
            <span className="text-[10px] uppercase tracking-widest text-indigo-300 font-extrabold mt-2 ml-0.5 leading-none">Manufacturer Portal</span>
          </div>
        </div>
        <div className="relative z-10 space-y-8 mt-auto">
          <div>
            <h1 className="text-4xl font-extrabold text-white leading-tight mb-4">Reset Your Password</h1>
            <p className="text-indigo-100/50 leading-relaxed text-lg">Enter your email and we'll send you a link to get back into your account.</p>
          </div>
        </div>
        <div className="relative z-10 mt-auto pt-8 border-t border-indigo-500/20">
          <p className="text-xs text-indigo-100/40">Secure password reset with encrypted links</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-gray-50/50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, #6366f1 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
        <div className="w-full max-w-md relative z-10">
          <div className="lg:hidden flex flex-col items-center gap-1.5 mb-10 justify-center">
            <img src="/assets/logo/logo.svg" className="h-9 w-auto object-contain" alt="ShoplyCircle Logo" />
            <span className="text-[10px] uppercase tracking-widest text-indigo-600 font-extrabold leading-none">Manufacturer Portal</span>
          </div>
          <Card className="w-full border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="text-center pb-4 px-6 pt-8">
              <CardTitle className="text-2xl font-bold text-gray-900">Forgot Password</CardTitle>
              <p className="text-gray-500">We'll send a reset link to your email</p>
            </CardHeader>
            <CardContent className="px-6 pb-8">
              {sent ? (
                <div className="text-center space-y-4 py-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-indigo-600" />
                  </div>
                  <p className="text-gray-900 font-semibold text-lg">Check your email</p>
                  <p className="text-gray-500 text-sm">We've sent a password reset link to <strong className="text-gray-700">{email}</strong></p>
                  <p className="text-gray-400 text-xs">Didn't receive it? Check your spam folder or try again.</p>
                  <Link to="/login" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium mt-2">
                    <ArrowLeft className="w-4 h-4" /> Back to login
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-bold text-gray-700 ml-1">Email Address</label>
                    <div className="relative group">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-indigo-600 transition-colors" />
                      <Input
                        id="email" type="email" required autoComplete="email"
                        placeholder="you@manufacturer.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="pl-12 h-13 py-3 rounded-xl bg-gray-50 border-gray-200 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-600 transition-all shadow-sm hover:shadow-md"
                      />
                    </div>
                  </div>
                  <Button type="submit" disabled={loading}
                    className="w-full h-14 rounded-2xl bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-bold text-lg shadow-xl shadow-indigo-600/30 transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] active:scale-100 flex items-center justify-center gap-2"
                  >
                    {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</> : 'Send Reset Link'}
                  </Button>
                  <p className="text-center text-sm text-gray-500 mt-4">
                    <Link to="/login" className="text-indigo-600 font-bold hover:text-indigo-700 hover:underline transition-colors inline-flex items-center gap-1">
                      <ArrowLeft className="w-4 h-4" /> Back to login
                    </Link>
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
          <div className="flex items-center justify-center gap-2 mt-6 text-xs text-gray-400">
            <Shield className="h-4 w-4 text-indigo-400" />
            <span>Secure & encrypted authentication</span>
          </div>
        </div>
      </div>
    </div>
  );
}
